import clsx from "clsx";
import { ReactComponent as CollapseIcon } from "icons/collapse.svg";
import { ReactComponent as ArrowDown } from "icons/dictionary/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "icons/dictionary/ArrowUp.svg";
import { ReactComponent as ExpandIcon } from "icons/expand.svg";
import PropTypes from "prop-types";
import React, {
  createRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeTree } from "react-vtree";
import { getScrollbarWidth } from "utils";
import handleKeyboardClick from "utils/handleKeyboardClick";

export const createHeaders = (headers) => {
  return headers.map((item) => ({
    text: item.title,
    ref: createRef(null),
  }));
};

export const getTotalOffsetLeft = (element) => {
  let totalOffset = 0;
  while (element) {
    totalOffset += element.offsetLeft || 0;
    element = element.offsetParent;
  }
  return totalOffset;
};

export const countNodes = (nodes) => {
  let count = 0;
  nodes.forEach((n) => {
    count++;
    if (n.children) {
      count += countNodes(n.children);
    }
  });
  return count;
};

export const getTreeIds = (children, ids, value) => {
  children?.forEach((child) => {
    if (child.children && child.children.length !== 0) {
      ids[child.id] = value;
      getTreeIds(child.children, ids, value);
    }
  });
};

export const handleExpandAll = async (data, tree) => {
  const ids = {};
  getTreeIds(data?.tree?.children, ids, true);
  await tree.treeRef.current.state.recomputeTree({
    opennessState: ids,
    refreshNodes: true,
    useDefaultOpenness: false,
  });
};

export const handleCollapseAll = async (data, tree) => {
  const ids = {};
  getTreeIds(data?.tree?.children, ids, false);
  await tree.treeRef.current.state.recomputeTree({
    opennessState: ids,
    refreshNodes: true,
    useDefaultOpenness: false,
  });
};

export const columnExpansion = (data, tree) => {
  if (tree.totalNodesOpened === tree.totalNodesOpenedDefault) {
    return (
      <span className="expansion">
        <ExpandIcon
          className="expansion_icon expand_all"
          alt="expand all"
          data-testid="expand-all"
          onClick={(e) => {
            handleExpandAll(data, tree);
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
        />
        <span className="tooltip">Expand all</span>
      </span>
    );
  }
  return (
    <span className="expansion">
      <CollapseIcon
        className="expansion_icon collapse_all"
        alt="collapse all"
        data-testid="collapse-all"
        onClick={(e) => {
          handleCollapseAll(data, tree);
          e.preventDefault();
          e.stopPropagation();
          return false;
        }}
      />
      <span className="tooltip">Collapse all</span>
    </span>
  );
};

const Tree = ({
  data,
  renderRow,
  columns,
  resizable,
  sortable = false,
  sortColumn,
  sortOrder,
  onRowPrerender,
  onSort,
  onScroll,
  itemSize,
  displayHeader = true,
  tree = {}, // Use this context to maintain state across multiple redraws
  openToLevel = 1,
  widthAdjust = 0,
  containerWidth,
}) => {
  const scrollbarWidth = getScrollbarWidth();
  const treeRef = useRef(null);
  const headerRef = useRef(null);
  const tableRef = useRef(null);
  const headers = createHeaders(columns);
  const [activeIndex, setActiveIndex] = useState(null);
  const [maxWidth, setMaxWidth] = useState(NaN);
  const [maxScreenAreaWidth, setmaxScreenAreaWidth] = useState(NaN);
  const [headerWidth, setHeaderWidth] = useState(
    window.innerWidth - widthAdjust
  );
  const [, setRedraw] = useState(0);

  const determineWidths = () => {
    if (containerWidth === -1) {
      return;
    }
    let maxWidth = columns.reduce(
      (total, column) => total + parseInt(column.width),
      0
    );
    let maxScreenAreaWidth = maxWidth;
    const width = containerWidth ? containerWidth : document.body.offsetWidth;
    if (maxScreenAreaWidth < width) {
      maxScreenAreaWidth = width - widthAdjust;
      maxWidth = maxScreenAreaWidth;
    }
    if (maxWidth > width) {
      maxWidth = width;
      maxWidth -= widthAdjust;
    }
    setMaxWidth(maxWidth);
    setmaxScreenAreaWidth(maxScreenAreaWidth);
    setHeaderWidth(window.innerWidth - widthAdjust);
  };

  const treeWalker = function* (refresh) {
    tree.totalNodesOpened = 0;
    const stack = [];

    stack.push({
      nestingLevel: 0,
      node: data?.tree,
    });

    while (stack.length !== 0) {
      const { node, nestingLevel } = stack.pop();
      const deepCopyNode = { ...node };
      if (!deepCopyNode.children) {
        deepCopyNode.children = [];
      }
      if (onRowPrerender) {
        if (refresh) {
          onRowPrerender({
            data,
            isRoot: nestingLevel === 0,
            isLeaf: deepCopyNode.children.length === 0,
            isOpenByDefault:
              nestingLevel < openToLevel ||
              data.expandedNodes?.includes(deepCopyNode.id),
            nestingLevel,
            ...deepCopyNode,
          });
        }
      }
      const isOpened = yield refresh
        ? {
            data,
            isRoot: nestingLevel === 0,
            isLeaf: deepCopyNode.children.length === 0,
            isOpenByDefault:
              nestingLevel < openToLevel ||
              data.expandedNodes?.includes(deepCopyNode.id),
            nestingLevel,
            ...deepCopyNode,
          }
        : deepCopyNode.id;
      if (isOpened) {
        tree.totalNodesOpened++;
      }
      if (deepCopyNode.children.length !== 0 && isOpened) {
        for (let i = deepCopyNode.children.length - 1; i >= 0; i--) {
          stack.push({
            nestingLevel: nestingLevel + 1,
            node: deepCopyNode.children[i],
          });
        }
      }
    }
    if (tree.totalNodesOpenedDefault === undefined) {
      tree.totalNodesOpenedDefault = tree.totalNodesOpened;
    }
    // if its different it will always force a redraw which is when this is necessary
    setRedraw(tree.totalNodesOpened);
  };

  const mouseMove = useCallback(
    (e) => {
      headers.forEach((col, i) => {
        if (i === activeIndex) {
          const width = e.clientX - getTotalOffsetLeft(col.ref.current);
          if (width >= 10) {
            if (col.ref.current) {
              col.ref.current.style.width = `${width}px`;
              col.ref.current.style.maxWidth = `${width}px`;
              col.ref.current.style.minWidth = `${width}px`;
              col.width = `${width}px`;
              columns[i].width = `${width}px`;
              const tbody = tableRef.current.querySelectorAll(".tree_body_row");
              if (tbody) {
                tbody.forEach((tr, _rowIndex) => {
                  const tds = tr.querySelector(
                    `.tree_cell:nth-of-type(${activeIndex + 1})`
                  );
                  tds.style.width = `${width}px`;
                  tds.style.minWidth = `${width}px`;
                  tds.style.maxWidth = `${width}px`;
                });
              }
            }
          }
        }
      });
    },
    [activeIndex, headers, columns]
  );

  const removeListeners = useCallback(() => {
    window.removeEventListener("mousemove", mouseMove);
    window.removeEventListener("mouseup", mouseUp);
  }, []);

  const mouseUp = useCallback(() => {
    setActiveIndex(null);
    removeListeners();
    determineWidths();
    treeRef.current.state.recomputeTree({
      refreshNodes: true,
      useDefaultOpenness: false,
    });
  }, [setActiveIndex, removeListeners]);

  const mouseDown = (index) => {
    if (!resizable) {
      return;
    }
    setActiveIndex(index);
  };

  const sortIcon = () => (
    <span className="sort_icon" data-testid="sort-icon">
      {sortOrder === "DESC" ? <ArrowUp /> : <ArrowDown />}
    </span>
  );

  const gapFiller = (sortable) => (
    <span className="gap_filler">
      {sortable ? (
        <span className="sort_icon">
          <ArrowDown />
        </span>
      ) : (
        <></>
      )}
    </span>
  );

  useEffect(() => {
    if (activeIndex !== null) {
      window.addEventListener("mousemove", mouseMove);
      window.addEventListener("mouseup", mouseUp);
    }

    determineWidths();

    tree.treeRef = treeRef; // Getting this allows outside components access to Tree methods, namely recomputeTree for expand/collapse all
    tree.maxWidth = maxScreenAreaWidth - scrollbarWidth;
    tree.currentRowWidth = 0;
    tree.headers = headers;

    const handleScroll = (event) => {
      if (headerRef.current) {
        headerRef.current.style.left = `-${event.target.scrollLeft}px`;
      }
      if (onScroll) {
        onScroll(event);
      }
    };

    const element = tableRef?.current?.children[0]?.children[0];
    if (element) {
      element.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (element) {
        element.removeEventListener("scroll", handleScroll);
      }
      removeListeners();
    };
  }, [activeIndex, mouseMove, mouseUp, removeListeners]);

  useLayoutEffect(() => {
    const resizeEventHandler = () => {
      determineWidths();
    };
    window.addEventListener("resize", resizeEventHandler);
    return () => {
      window.removeEventListener("resize", resizeEventHandler);
    };
  }, []);

  return (
    <>
      {displayHeader ? (
        <div className="tree_header_container" style={{ width: headerWidth }}>
          <div className="tree_header" ref={headerRef}>
            <div
              className="tree_header_row"
              style={{ width: maxScreenAreaWidth }}
            >
              {headers.map(({ ref, text }, i) => {
                const columnStyle = {
                  minWidth: columns[i].width,
                };
                if (i === columns.length - 1) {
                  columnStyle.flexGrow = 1;
                } else {
                  columnStyle.minWidth = columns[i].width;
                  columnStyle.maxWidth = columns[i].width;
                  columnStyle.flex = 1;
                }
                return (
                  <div
                    className={clsx(
                      "tree_cell tree_header_cell",
                      i === columns.length - 1 && "last",
                      sortable && columns[i].sortable !== false && "sortable"
                    )}
                    ref={ref}
                    key={text}
                    style={columnStyle}
                    onClick={
                      sortable && columns[i].sortable !== false
                        ? () => {
                            onSort(
                              columns[i].name,
                              columns[i].type,
                              columns[i]
                            );
                          }
                        : undefined
                    }
                    onKeyDown={handleKeyboardClick(() => {
                      if (sortable && columns[i].sortable !== false) {
                        onSort(columns[i].name, columns[i].type, columns[i]);
                      }
                    })}
                  >
                    <span>
                      {columns[i].controlHandler
                        ? columns[i].controlHandler(text)
                        : text}
                    </span>
                    {sortable && sortColumn === columns[i].name
                      ? sortIcon()
                      : gapFiller(sortable)}
                    {columns[i].columnExpansion ? (
                      columnExpansion(data, tree)
                    ) : (
                      <></>
                    )}
                    {resizable && i !== columns.length - 1 ? (
                      <button
                        type="button"
                        aria-label="Resize column"
                        onMouseDown={
                          columns[i].resizable !== false
                            ? () => mouseDown(i)
                            : undefined
                        }
                        className={clsx(
                          "column_divider",
                          columns[i].resizable !== false && "resize_handle"
                        )}
                      >
                        <div className="column_divider_inner"></div>
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
      <div className="tree" data-testid="tree" ref={tableRef}>
        <AutoSizer
          defaultWidth={100}
          defaultHeight={100}
          className="auto-sizer"
        >
          {({ height }) => (
            <VariableSizeTree
              treeWalker={treeWalker}
              height={height}
              width={maxWidth}
              ref={treeRef}
              itemSize={itemSize ? itemSize : (index) => (!index ? 0 : 24)}
            >
              {renderRow}
            </VariableSizeTree>
          )}
        </AutoSizer>
      </div>
    </>
  );
};

export default Tree;

Tree.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object]).isRequired,
  renderRow: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  onSort: PropTypes.func,
  onScroll: PropTypes.func,
  onRowPrerender: PropTypes.func,
  itemSize: PropTypes.func,
  resizable: PropTypes.bool,
  displayHeader: PropTypes.bool,
  sortable: PropTypes.bool,
  sortColumn: PropTypes.string,
  sortOrder: PropTypes.string,
  tree: PropTypes.oneOfType([PropTypes.object]),
  openToLevel: PropTypes.number,
  widthAdjust: PropTypes.number,
  containerWidth: PropTypes.number,
};
